/**
 * The screen after a successful apply / RSVP / purchase, rebuilt on the surface vocabulary.
 * Copy follows the event: curated → the host reviews; free → you're on the list; paid →
 * money is in escrow until greenlight. Summary shows date, map (exact once revealed),
 * venue, pledge and status; GET DIRECTIONS when the address is out.
 */
import React from 'react';
import { Panel, PanelHeader, PanelBody, KVLine, SurfaceButton, MUTED, GREEN } from '../surface';
import EventLocationMap from '../EventLocationMap';
import { money, whenLabel } from '../../utils/surfaceFormat';
import { whenLine, venueLine } from './EventHeader';
import { directionsUrl } from './EventWhereTab';

export default function EventSuccessScreen({ event, application, mobile = false, onDashboard, onBrowse }) {
  const curated = event?.mode === 'curated';
  const free = (event?.ticketPriceCents || 0) === 0 && event?.pricingMode !== 'pay_what_you_want';
  const pledge = application?.pledgeAmountCents || event?.ticketPriceCents || 0;
  const status = String(application?.status || 'pending').toUpperCase().replace(/_/g, ' ');
  const dir = directionsUrl(event);
  const hasMap = !!(event?.locationLatitude && event?.locationLongitude);
  return (
    <div className={`mx-auto flex w-full flex-col ${mobile ? 'gap-3' : 'gap-4'}`} style={{ maxWidth: 720, padding: mobile ? 12 : 0 }} data-testid="apply-success">
      <div className="flex flex-col items-center gap-3 border-2 border-black bg-white text-center" style={{ padding: mobile ? 24 : 32 }}>
        <span className="flex items-center justify-center border-4 border-black font-mono font-bold" style={{ width: 64, height: 64, fontSize: 28 }} aria-hidden="true">✓</span>
        <h2 className="font-mono font-bold uppercase" style={{ fontSize: mobile ? 18 : 22, letterSpacing: '0.05em', margin: 0 }}>
          {curated ? 'APPLICATION SENT' : free ? "YOU'RE ON THE LIST" : "YOU'RE IN"}
        </h2>
        <p className="font-mono" style={{ fontSize: 13, color: MUTED, maxWidth: 440, margin: 0, lineHeight: 1.6 }}>
          {curated
            ? 'The host reads every application and answers all of them, yes or no. You will hear back.'
            : free
              ? "We'll see you there. Your name is on the guest list."
              : 'Your money sits in escrow with Portal until the event is greenlit and 24h past doors.'}
        </p>
      </div>
      <Panel>
        <PanelHeader label={event?.title || 'EVENT'} right={status} />
        <PanelBody>
          <KVLine k="WHEN" v={whenLine(event) || whenLabel(event?.dateTime)} />
          {venueLine(event) && <KVLine k="VENUE" v={venueLine(event)} />}
          <KVLine k="LOCATION" v={event?.locationRevealed && event?.locationAddress ? event.locationAddress : `REVEALED AFTER ${event?.fundingType === 'guaranteed' ? 'PURCHASE' : 'GREENLIGHT'}`} />
          <KVLine k="YOUR PLEDGE" v={pledge === 0 ? 'FREE' : money(pledge)} color={pledge === 0 ? GREEN : undefined} />
          <KVLine k="STATUS" v={status} />
          {hasMap && (
            <EventLocationMap
              latitude={event.locationLatitude}
              longitude={event.locationLongitude}
              locationRevealed={!!event.locationRevealed}
              locationAddress={event.locationAddress}
              locationCity={event.locationCity}
              fundingType={event.fundingType}
              height={mobile ? 180 : 240}
            />
          )}
          {dir && <SurfaceButton variant="outline" size="sm" className="self-start" onClick={() => window.open(dir, '_blank', 'noopener,noreferrer')}>GET DIRECTIONS</SurfaceButton>}
        </PanelBody>
      </Panel>
      <div className={`flex ${mobile ? 'flex-col' : ''} gap-3`}>
        <SurfaceButton variant="fill" size="cta" className="flex-1" onClick={onDashboard} data-testid="go-dashboard">GO TO DASHBOARD</SurfaceButton>
        <SurfaceButton variant="outline" size="cta" className="flex-1" onClick={onBrowse}>BROWSE MORE EVENTS</SurfaceButton>
      </div>
    </div>
  );
}
